import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Shield, User, Mail, Loader2, AlertTriangle, Trash2, Sparkles } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastProvider";
import { useTutorial } from "../../contexts/TutorialContext";
import PageHeader from "../../components/collavue/PageHeader";
import { deleteAccount as deleteAccountApi } from "../../services/collavueApi";

export default function SettingsPage() {
  const { user, userProfile, updateUserProfile, resetPassword, logout } = useAuth();
  const { push } = useToast();
  const { start: startTutorial } = useTutorial();
  const nav = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [emailDigests, setEmailDigests] = useState(true);
  const [hotLeadAlerts, setHotLeadAlerts] = useState(true);
  const [creditAlerts, setCreditAlerts] = useState(true);
  const [creditGrants, setCreditGrants] = useState(true);
  const [reactivationEmails, setReactivationEmails] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!userProfile) return;
    setDisplayName(userProfile.displayName || "");
    setCompanyName(userProfile.companyName || "");
    setAutoSyncEnabled(userProfile.autoSyncEnabled !== false);
    setEmailDigests(userProfile.notifyEmailDigests !== false);
    setHotLeadAlerts(userProfile.notifyHotLeads !== false);
    setCreditAlerts(userProfile.notifyLowCredits !== false);
    setCreditGrants(userProfile.notifyCreditGrants !== false);
    setReactivationEmails(userProfile.notifyReactivation !== false);
  }, [userProfile]);

  const onSave = async () => {
    setSaving(true);
    try {
      await updateUserProfile({
        displayName,
        companyName,
        autoSyncEnabled,
        notifyEmailDigests: emailDigests,
        notifyHotLeads: hotLeadAlerts,
        notifyLowCredits: creditAlerts,
        notifyCreditGrants: creditGrants,
        notifyReactivation: reactivationEmails,
      });
      push({ title: "Saved", variant: "success" });
    } catch (e) {
      push({ title: "Save failed", desc: e.message, variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const onResetPassword = async () => {
    if (!user?.email) return;
    setResetting(true);
    try {
      await resetPassword(user.email);
      push({
        title: "Reset email sent",
        desc: "Check your inbox for a reset link.",
        variant: "success",
      });
    } catch (e) {
      push({ title: "Reset failed", desc: e.message, variant: "error" });
    } finally {
      setResetting(false);
    }
  };

  const onConfirmDelete = async () => {
    if (deleteText.trim().toUpperCase() !== "DELETE") {
      push({
        title: "Confirmation required",
        desc: 'Type DELETE exactly to confirm.',
        variant: "error",
      });
      return;
    }
    setDeleting(true);
    try {
      await deleteAccountApi("DELETE");
      push({
        title: "Account deleted",
        desc: "Your Collavue account has been permanently removed.",
        variant: "success",
      });
      try {
        await logout({ redirectTo: "/" });
      } catch {
        nav("/");
      }
    } catch (e) {
      push({ title: "Delete failed", desc: e.message, variant: "error" });
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">
      <PageHeader title="Settings" subtitle="Profile, alerts, and security." />

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-semibold flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-indigo-600" /> Profile
        </h2>
        <Field label="Display name" value={displayName} onChange={setDisplayName} />
        <Field label="Company name" value={companyName} onChange={setCompanyName} />
        <div className="mt-3">
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
            Email
          </label>
          <div className="text-sm text-slate-700 inline-flex flex-wrap items-center gap-2 break-all">
            <Mail className="w-4 h-4 text-slate-400 shrink-0" /> {user?.email}{" "}
            {user?.emailVerified ? (
              <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                verified
              </span>
            ) : (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                unverified
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-semibold mb-4">Daily auto-sync</h2>
        <Toggle
          checked={autoSyncEnabled}
          onChange={setAutoSyncEnabled}
          label="Run daily auto-sync"
          desc="Master switch. When off, no campaign syncs overnight and no credits are spent — whatever each campaign's own auto-sync setting says."
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-semibold mb-4">Notifications</h2>
        <Toggle
          checked={emailDigests}
          onChange={setEmailDigests}
          label="Daily sync emails"
          desc="Your daily lead digest: new leads found overnight, hot-lead count, credits used, and the results of any saved lead-assistant commands."
        />
        <Toggle
          checked={hotLeadAlerts}
          onChange={setHotLeadAlerts}
          label="Hot lead alerts"
          desc="Notify me when a hot lead is found."
        />
        <Toggle
          checked={creditAlerts}
          onChange={setCreditAlerts}
          label="Low credit alerts"
          desc="Warn me when credits drop below 10."
        />
        <Toggle
          checked={creditGrants}
          onChange={setCreditGrants}
          label="Monthly credit grant emails"
          desc="Notify me when monthly free credits are added."
        />
        <Toggle
          checked={reactivationEmails}
          onChange={setReactivationEmails}
          label="Reactivation emails"
          desc="Occasional check-ins (with bonus credits) if I've been away a while."
        />
        <p className="mt-4 text-xs text-slate-500">
          Every notification email includes a one-click unsubscribe link. We
          only send to verified addresses.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-semibold flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-indigo-600" /> Security
        </h2>
        <button
          onClick={onResetPassword}
          disabled={resetting}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
        >
          {resetting && <Loader2 className="w-4 h-4 animate-spin" />}
          Send password reset email
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-semibold flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-indigo-600" /> Product tour
        </h2>
        <p className="text-sm text-slate-600 mb-3">
          Replay the short tour that highlights the main parts of Collavue.
        </p>
        <button
          onClick={() => {
            nav("/dashboard");
            setTimeout(() => startTutorial(), 50);
          }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold hover:bg-slate-50"
        >
          <Sparkles className="w-4 h-4" /> Replay tutorial
        </button>
      </div>

      <button
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
      >
        <Save className="w-4 h-4" /> Save settings
      </button>

      <div className="bg-white border border-rose-200 rounded-2xl p-5 mt-6">
        <h2 className="font-semibold flex items-center gap-2 mb-2 text-rose-700">
          <AlertTriangle className="w-4 h-4" /> Danger zone
        </h2>
        <p className="text-sm text-slate-600 mb-4">
          Permanently delete your Collavue account, campaigns, leads, and brand
          voice. This action cannot be undone.
        </p>
        <button
          onClick={() => {
            setDeleteText("");
            setDeleteOpen(true);
          }}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50"
        >
          <Trash2 className="w-4 h-4" /> Delete my account
        </button>
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  Permanently delete your account?
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  This deletes your account, campaigns, leads, replies, brand
                  voice, and notification history. <strong>Your remaining
                  credits will not be refunded.</strong> This cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">
                Type <span className="font-mono">DELETE</span> to confirm
              </label>
              <input
                value={deleteText}
                onChange={(e) => setDeleteText(e.target.value)}
                placeholder="DELETE"
                disabled={deleting}
                autoFocus
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
                className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={onConfirmDelete}
                disabled={deleting || deleteText.trim().toUpperCase() !== "DELETE"}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 disabled:opacity-60"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div className="mt-3">
      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
        {label}
      </label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      />
    </div>
  );
}

function Toggle({ checked, onChange, label, desc }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-indigo-600"
      />
      <div>
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
    </label>
  );
}
